/**
 * useVideoUpload - Custom hook for video compression and upload
 */
import { useState, useRef, useCallback } from 'react';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';

const storage = getStorage();

// Processing states
export const PROCESSING_STATE = {
  IDLE: 'idle',
  COMPRESSING: 'compressing',
  UPLOADING: 'uploading'
};

export function useVideoUpload({ compressionTargetSizeMB = 0.9, onVideoUploaded }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProcessingStep, setCurrentProcessingStep] = useState(PROCESSING_STATE.IDLE);
  const [progressPercent, setProgressPercent] = useState(0);
  const [processingError, setProcessingError] = useState(null);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const activeXhrRef = useRef(null);
  const processingCancelledRef = useRef(false);

  const cancelProcessing = useCallback(() => {
    processingCancelledRef.current = true;
    
    if (activeXhrRef.current) {
      if (typeof activeXhrRef.current.abort === 'function') {
        activeXhrRef.current.abort();
      }
      activeXhrRef.current = null;
    }
    
    setIsProcessing(false);
    setCurrentProcessingStep(PROCESSING_STATE.IDLE);
    setProgressPercent(0);
  }, []);

  // Compress video using MediaRecorder API
  const compressVideo = useCallback((file) => {
    return new Promise((resolve, reject) => {
      console.log('Starting compression for file:', file.name, file.size);
      setCurrentProcessingStep(PROCESSING_STATE.COMPRESSING);
      
      let videoElement = null;
      let progressInterval = null;
      
      const cleanup = () => {
        console.log('Cleaning up compression resources');
        if (progressInterval) {
          clearInterval(progressInterval);
          progressInterval = null;
        }
        
        if (videoElement && videoElement.src) {
          videoElement.pause();
          URL.revokeObjectURL(videoElement.src);
          videoElement = null;
        }
      };
      
      // Skip compression for small files
      if (file.size < compressionTargetSizeMB * 1024 * 1024) {
        console.log('File is small enough, skipping compression');
        setProgressPercent(100);
        resolve({ compressedFile: file, originalSize: file.size, compressedSize: file.size });
        return;
      }

      videoElement = document.createElement('video');
      videoElement.preload = 'metadata';
      videoElement.src = URL.createObjectURL(file);
      videoElement.muted = true;
      
      setProgressPercent(5);
      
      videoElement.onloadedmetadata = () => {
        const originalDuration = videoElement.duration;
        const originalSize = file.size;
        console.log(`Original video: ${originalSize / 1024 / 1024} MB, Duration: ${originalDuration}s`);
        
        try {
          const targetSizeInBits = compressionTargetSizeMB * 8 * 1024 * 1024;
          const targetBitrate = Math.min(
            targetSizeInBits / originalDuration,
            1000000
          );
          
          setProgressPercent(15);
          
          const options = {
            mimeType: 'video/webm;codecs=vp9',
            videoBitsPerSecond: targetBitrate,
            audioBitsPerSecond: 128000
          };
          
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options.mimeType = 'video/webm;codecs=vp8';
          }
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options.mimeType = 'video/webm';
          }
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options.mimeType = '';
          }
          
          videoElement.play();
          
          const mediaStream = videoElement.captureStream();
          const mediaRecorder = new MediaRecorder(mediaStream, options);
          const chunks = [];
          
          mediaRecorder.start();
          console.log('Started recording with audio preservation');
          
          progressInterval = setInterval(() => {
            if (videoElement.currentTime > 0 && !videoElement.paused && !videoElement.ended) {
              const progress = Math.min(Math.round((videoElement.currentTime / originalDuration) * 80) + 15, 95);
              setProgressPercent(progress);
            }
          }, 100);
          
          mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              chunks.push(e.data);
            }
          };
          
          mediaRecorder.onstop = () => {
            cleanup();
            setProgressPercent(95);
            
            const blob = new Blob(chunks, { type: 'video/webm' });
            const compressedSize = blob.size;
            
            setProgressPercent(100);
            
            console.log(`Compressed video (with audio): ${compressedSize / 1024 / 1024} MB, Reduction: ${((1 - (compressedSize / originalSize)) * 100).toFixed(2)}%`);
            
            if (compressedSize > originalSize * 1.1) {
              console.log('Compressed version is larger, using original video');
              resolve({ compressedFile: file, originalSize, compressedSize: originalSize });
              return;
            }
            
            const compressedFile = new File([blob], file.name, {
              type: 'video/webm',
              lastModified: new Date().getTime()
            });
            
            resolve({ compressedFile, originalSize, compressedSize });
          };
          
          videoElement.onended = () => {
            console.log('Video playback ended, stopping recorder');
            if (mediaRecorder && mediaRecorder.state !== 'inactive') {
              mediaRecorder.stop();
            }
          };
          
        } catch (err) {
          console.error('Error setting up media recorder:', err);
          cleanup();
          console.log('Using original video due to compression setup error');
          resolve({ compressedFile: file, originalSize: file.size, compressedSize: file.size });
        }
      };
      
      videoElement.onerror = (e) => {
        console.error('Error loading video metadata:', e);
        cleanup();
        setCurrentProcessingStep(PROCESSING_STATE.IDLE);
        reject(new Error('Failed to load video metadata'));
      };
    });
  }, [compressionTargetSizeMB]);

  // Upload to GCS using signed URL
  const uploadToGCS = useCallback((file, url) => {
    return new Promise((resolve, reject) => {
      console.log('Starting GCS upload with signed URL:', url);
      
      const xhr = new XMLHttpRequest();
      activeXhrRef.current = xhr;
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setProgressPercent(progress);
        }
      };
      
      xhr.onload = () => {
        activeXhrRef.current = null;
        
        if (processingCancelledRef.current) {
          console.log('Upload was cancelled, ignoring completion');
          return reject(new Error('Upload cancelled'));
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('GCS upload successful with status:', xhr.status);
          
          const httpsUrl = url.split('?')[0];
          let permanentUrl = httpsUrl;
          if (httpsUrl.includes('storage.googleapis.com/')) {
            const pathAfterDomain = httpsUrl.split('storage.googleapis.com/')[1];
            permanentUrl = `gs://${pathAfterDomain}`;
          }
          
          resolve({ permanentUrl });
        } else {
          console.error('GCS upload failed:', xhr.status, xhr.statusText);
          reject(new Error(`GCS upload failed with status ${xhr.status}`));
        }
      };
      
      xhr.onerror = () => {
        activeXhrRef.current = null;
        if (processingCancelledRef.current) {
          return reject(new Error('Upload cancelled'));
        }
        reject(new Error('Network error during file upload'));
      };
      
      xhr.onabort = () => {
        activeXhrRef.current = null;
        reject(new Error('Upload aborted'));
      };
      
      xhr.open('PUT', url, true);
      xhr.send(file);
    });
  }, []);

  // Upload to Firebase Storage
  const uploadToFirebase = useCallback((file, uploadPath) => {
    return new Promise((resolve, reject) => {
      console.log('Attempting upload using Firebase SDK to:', uploadPath);
      
      const timestamp = Date.now();
      const uniqueFilename = `${timestamp}_${file.name}`;
      
      let storagePath = 'templates/videoTemplates';
      try {
        if (uploadPath && uploadPath.includes('/')) {
          const url = new URL(uploadPath);
          const pathMatch = url.pathname.match(/\/(.+?)\/[^\/]+$/);
          if (pathMatch && pathMatch[1]) {
            storagePath = pathMatch[1];
          }
        }
      } catch (e) {
        console.warn('Could not parse upload path, using default:', e);
      }
      
      const storageRef = ref(storage, `${storagePath}/${uniqueFilename}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      activeXhrRef.current = { abort: () => uploadTask.cancel() };
      
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setProgressPercent(progress);
        },
        (error) => {
          console.error('Firebase upload error:', error);
          activeXhrRef.current = null;
          reject(error);
        },
        () => {
          console.log('Firebase upload successful');
          activeXhrRef.current = null;
          
          const bucket = storage.app.options.storageBucket;
          const gsUrl = `gs://${bucket}/${storagePath}/${uniqueFilename}`;
          resolve(gsUrl);
        }
      );
    });
  }, []);

  // Main upload handler
  const handleUpload = useCallback(async (videoFile, uploadUrl, videoDuration, fileName) => {
    if (!videoFile) {
      setProcessingError('Please select a video file first');
      return;
    }
    
    if (!uploadUrl) {
      setProcessingError('No upload URL provided');
      return;
    }
    
    console.log('[useVideoUpload] Starting upload process');
    
    setIsProcessing(true);
    setProcessingError(null);
    setProgressPercent(0);
    processingCancelledRef.current = false;
    
    try {
      // Step 1: Compress
      let compressionResult;
      try {
        setCurrentProcessingStep(PROCESSING_STATE.COMPRESSING);
        compressionResult = await compressVideo(videoFile);
      } catch (compressionError) {
        console.error('[useVideoUpload] Compression failed:', compressionError);
        compressionResult = {
          compressedFile: videoFile,
          originalSize: videoFile.size,
          compressedSize: videoFile.size
        };
      }
      
      const { compressedFile, originalSize, compressedSize } = compressionResult;
      
      if (processingCancelledRef.current) {
        return;
      }
      
      // Step 2: Upload
      setCurrentProcessingStep(PROCESSING_STATE.UPLOADING);
      setProgressPercent(0);
      
      let permanentUrl;
      try {
        const gcsResult = await uploadToGCS(compressedFile, uploadUrl);
        permanentUrl = gcsResult.permanentUrl;
        setSuccessMessage('Video uploaded successfully!');
      } catch (directUploadError) {
        console.warn('[useVideoUpload] Direct GCS upload failed, trying Firebase SDK:', directUploadError);
        
        if (processingCancelledRef.current) {
          setIsProcessing(false);
          return;
        }
        
        permanentUrl = await uploadToFirebase(compressedFile, uploadUrl);
        setSuccessMessage('Video uploaded successfully!');
      }
      
      // Notify parent
      if (onVideoUploaded) {
        const videoMetadata = {
          videoUrl: permanentUrl,
          originalSize,
          compressedSize,
          duration: videoDuration,
          fileName: fileName
        };
        onVideoUploaded(videoMetadata);
      }
      
      setVideoUploaded(true);
      setIsProcessing(false);
      setCurrentProcessingStep(PROCESSING_STATE.IDLE);
      
    } catch (error) {
      console.error('[useVideoUpload] Error:', error);
      
      if (!processingCancelledRef.current) {
        setProcessingError(error.message || 'Failed to process video');
      }
      
      setIsProcessing(false);
      setCurrentProcessingStep(PROCESSING_STATE.IDLE);
    }
  }, [compressVideo, uploadToGCS, uploadToFirebase, onVideoUploaded]);

  const resetUploadState = useCallback(() => {
    setVideoUploaded(false);
    setSuccessMessage(null);
    setProcessingError(null);
  }, []);

  return {
    isProcessing,
    currentProcessingStep,
    progressPercent,
    processingError,
    videoUploaded,
    successMessage,
    handleUpload,
    cancelProcessing,
    resetUploadState,
    setVideoUploaded,
    setProcessingError
  };
}

// Helper function to convert gs:// URL to HTTPS
export function convertGsUrlToHttps(gsUrl) {
  if (gsUrl && gsUrl.startsWith('gs://')) {
    const pathAfterGs = gsUrl.substring(5);
    
    if (pathAfterGs.includes('.firebasestorage.app/') || pathAfterGs.includes('.appspot.com/')) {
      const parts = pathAfterGs.split('/');
      const bucketName = parts[0];
      const filePath = parts.slice(1).join('/');
      const encodedPath = encodeURIComponent(filePath);
      return `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/${encodedPath}?alt=media`;
    } else {
      return `https://storage.googleapis.com/${pathAfterGs}`;
    }
  }
  return gsUrl;
}

// Format file size helper
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Format duration helper
export function formatDuration(seconds) {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

